import ButtonStyles from "@src/deprecatedDesignSystem/styles/ButtonStyles";
import { deprecatedTones } from "@src/deprecatedDesignSystem/styles/deprecatedColors";
import MiscStyles from "@src/deprecatedDesignSystem/styles/MiscStyles";
import { pluralize } from "@utils/strings";
import { css, StyleDeclaration, StyleSheet } from "aphrodite";
import { CSSProperties, FC, useMemo } from "react";
import DeprecatedIcon from "@src/deprecatedDesignSystem/components/DeprecatedIcon";
import useDropdown from "@hooks/useDropdown";
import { cn } from "@src/ui/lib/utils";
import { buttonVariants } from "@src/ui/button";
import Text from "@src/ui/text";

type Props = {
  selectedCount: number;
  label?: string;
  labelPluralizationSuffix?: string;
  getMenuComponent?: (close: () => void) => JSX.Element;
  children?: JSX.Element;
  width?: React.CSSProperties["width"];
  showIcon?: boolean;
  filledStyle?: boolean;
  clearFilter?: () => void;
  showFilterCount?: boolean;
  styleOverrides?: CSSProperties;
  styleDeclaration?: StyleDeclaration;
};

export const FilterDropdown: FC<Props> = ({
  selectedCount,
  label = "Filter",
  labelPluralizationSuffix = "s",
  getMenuComponent,
  children,
  width = 300,
  filledStyle = false,
  clearFilter,
  showFilterCount = true,
  styleOverrides,
  styleDeclaration,
}) => {
  const { dropdownMenuRef, isDropdownVisible, setIsDropdownVisible } =
    useDropdown();
  const menuComponent = useMemo(() => {
    if (getMenuComponent)
      return getMenuComponent(() => setIsDropdownVisible(false));
    return children;
  }, [getMenuComponent, setIsDropdownVisible, children]);

  const buttonStyles = useMemo(() => {
    if (filledStyle) {
      return css(
        isDropdownVisible && styles.filterVisible,
        selectedCount > 0 && ButtonStyles.enabledFilledButton,
        styleDeclaration,
      );
    }
    return css(
      isDropdownVisible && styles.filterVisible,
      selectedCount > 0 && ButtonStyles.enabledFilledButton,
      styleDeclaration,
    );
  }, [isDropdownVisible, selectedCount, filledStyle, styleDeclaration]);
  return (
    <div
      className={cn(
        buttonVariants({ variant: "outline" }),
        "cursor-pointer",
        css([styles.filterContainer]),
        buttonStyles,
      )}
      ref={dropdownMenuRef}
      style={styleOverrides}
      onClick={() => setIsDropdownVisible(!isDropdownVisible)}
    >
      <Text type="P2">{`${
        showFilterCount && selectedCount > 0 ? selectedCount + " " : ""
      }${pluralize(label, selectedCount, labelPluralizationSuffix)}`}</Text>
      {clearFilter && selectedCount > 0 ? (
        <DeprecatedIcon
          type="close"
          style={{ marginLeft: 4 }}
          color={deprecatedTones.gray11}
          onClick={clearFilter}
        />
      ) : (
        <DeprecatedIcon type="chevron-down" iconSize={24} />
      )}
      {isDropdownVisible && menuComponent && (
        <div className={css(styles.filter)} style={{ width }}>
          {menuComponent}
        </div>
      )}
    </div>
  );
};

const styles = StyleSheet.create({
  filterContainer: {
    position: "relative",
  },
  filter: {
    position: "absolute",
    backgroundColor: deprecatedTones.white,
    marginTop: 12,
    right: 0,
    boxShadow: "0 8px 28px -12px rgba(0, 0, 0, 0.4)",
    borderRadius: 8,
    border: `1px solid ${deprecatedTones.gray4Alpha}`,
    zIndex: 10,
  },
  filterVisible: {
    ...MiscStyles.focus,
  },
  buttonIcon: {
    marginRight: "4px",
    display: "flex",
    alignItems: "center",
  },
  buttonContents: {
    display: "flex",
    alignItems: "center",
    paddingLeft: 8,
    backgroundColor: deprecatedTones.white,
    borderRadius: 12,
    height: 36,
    border: "1px solid",
    borderColor: deprecatedTones.gray5Alpha,
    fontSize: 14,
    cursor: "pointer",
    ":hover": {
      backgroundColor: deprecatedTones.gray4Alpha,
    },
  },
});

export default FilterDropdown;
